import { useCallback, useEffect, useRef } from 'react'

import { useSearchParams } from 'react-router-dom'

import { ChainId } from '@src/configs/chainInfo'
import { useEmbedTokenDefaults } from '@src/hooks/useEmbedTokenDefaults'
import { ExchangePage } from '@src/pages'
import { useExchangeDataContext } from '@src/pages/Exchange/context/ExchangeDataContext'
import { useExchangeState } from '@src/pages/Exchange/context/ExchangeStateContext'
import { Address, IToken } from '@src/types/token'
import { parseChains, parseTokenParam } from '@src/utils/urlParams'
import { useConfigStore, useTokensStore } from '@src/zustand_stores'

// Define the interface to match ExchangePage props
interface EmbedPageProps {
    hideAIInput?: boolean
    fromParam?: string | null
    toParam?: string | null
    chainsParam?: string | null
    destinationWalletParam?: string | null
}

const EmbedPage = ({
    hideAIInput = false,
    fromParam = null,
    toParam = null,
    chainsParam = null,
    destinationWalletParam = null,
}: EmbedPageProps) => {
    const [searchParams] = useSearchParams()

    // Use ref to track initialization
    const initialized = useRef(false)

    const { updateSellData, updateBuyData, sellData, setDestinationAddress } = useExchangeDataContext()
    const { setChainId, setIsCustomRecipientWallet } = useExchangeState()
    const tokens = useTokensStore(state => state.tokens)
    const updateConfig = useConfigStore(state => state.updateConfig)

    // Get existing URL parameters (legacy format)
    const sellChainParam = searchParams.get('sellChain')
    const buyChainParam = searchParams.get('buyChain')
    const sellTokenParam = searchParams.get('sellToken')
    const buyTokenParam = searchParams.get('buyToken')

    // Use our new hook for default tokens
    useEmbedTokenDefaults()

    // Function to find a token by address and chainId
    const findTokenByAddress = useCallback(
        (address: Address, chainId: number): IToken | undefined => {
            return tokens.find(
                token => token.address.toLowerCase() === address.toLowerCase() && token.chainId === chainId
            )
        },
        [tokens]
    )

    // Handle 'chains' parameter once to restrict available chains
    useEffect(() => {
        const supportedChains = chainsParam ? parseChains(chainsParam) : null
        if (supportedChains) {
            // Update the config to restrict available networks
            updateConfig({
                restrictedChains: supportedChains,
            })
        }
    }, [chainsParam, updateConfig])

    // Handle destination wallet parameter if provided
    useEffect(() => {
        if (destinationWalletParam && destinationWalletParam.trim() !== '') {
            // Set the custom destination address from URL
            setDestinationAddress(destinationWalletParam.trim() as Address)
            // Enable custom recipient wallet mode
            setIsCustomRecipientWallet(true)

            console.log(`Setting custom destination wallet: ${destinationWalletParam}`)
        }
    }, [destinationWalletParam, setDestinationAddress, setIsCustomRecipientWallet])

    // Parse tokens and update exchange state only once on initial load
    useEffect(() => {
        // Skip if we've already initialized
        if (initialized.current) {
            return
        }

        // Wait for tokens to be loaded
        if (!tokens || tokens.length === 0) {
            return
        }

        // Process the chains param first to ensure we have the restriction set
        const supportedChains = chainsParam ? parseChains(chainsParam) : null

        // Set initial tokens based on URL parameters
        let didSetSellToken = false
        let didSetBuyToken = false

        // First check for the new format (from/to)
        // Handle 'from' token if provided
        const fromToken = fromParam ? parseTokenParam(fromParam) : null
        if (fromToken) {
            // Verify the token's chain is allowed if we have restricted chains
            const isChainAllowed = !supportedChains || supportedChains.includes(fromToken.chainId)

            if (isChainAllowed) {
                const token = findTokenByAddress(fromToken.tokenAddress, fromToken.chainId)
                if (token) {
                    updateSellData({
                        token,
                        chainId: fromToken.chainId,
                    })
                    setChainId(fromToken.chainId)
                    didSetSellToken = true
                }
            } else {
                console.log(`Token from chain ${fromToken.chainId} not allowed. Restricted to: ${supportedChains}`)
                // Find a valid token from allowed chains instead
                if (supportedChains && supportedChains.length > 0) {
                    const fallbackTokens = tokens.filter(t => supportedChains.includes(t.chainId))
                    // Prefer stable coins
                    const stableToken = fallbackTokens.find(
                        t => ['USDC', 'USDT', 'DAI', 'BUSD'].includes(t.symbol) && supportedChains.includes(t.chainId)
                    )

                    if (stableToken || fallbackTokens.length > 0) {
                        const validToken = stableToken || fallbackTokens[0]
                        console.log(`Using fallback token: ${validToken.symbol} (${validToken.chainId})`)
                        updateSellData({
                            token: validToken,
                            chainId: validToken.chainId,
                        })
                        setChainId(validToken.chainId)
                        didSetSellToken = true
                    }
                }
            }
        }
        // Otherwise, check for legacy format (sellChain/sellToken)
        else if (sellChainParam && sellTokenParam) {
            const sellChainId = parseInt(sellChainParam, 10)
            if (!isNaN(sellChainId) && Object.values(ChainId).includes(sellChainId)) {
                // Verify the chain is allowed if we have restricted chains
                const isChainAllowed = !supportedChains || supportedChains.includes(sellChainId)

                if (isChainAllowed) {
                    const token = findTokenByAddress(sellTokenParam as Address, sellChainId)
                    if (token) {
                        updateSellData({
                            token,
                            chainId: sellChainId,
                        })
                        setChainId(sellChainId)
                        didSetSellToken = true
                    }
                } else {
                    console.log(`Sell chain ${sellChainId} not allowed. Restricted to: ${supportedChains}`)
                    // Find a valid token from allowed chains instead
                    if (supportedChains && supportedChains.length > 0) {
                        const fallbackTokens = tokens.filter(t => supportedChains.includes(t.chainId))
                        // Prefer stable coins
                        const stableToken = fallbackTokens.find(
                            t =>
                                ['USDC', 'USDT', 'DAI', 'BUSD'].includes(t.symbol) &&
                                supportedChains.includes(t.chainId)
                        )

                        if (stableToken || fallbackTokens.length > 0) {
                            const validToken = stableToken || fallbackTokens[0]
                            console.log(`Using fallback token: ${validToken.symbol} (${validToken.chainId})`)
                            updateSellData({
                                token: validToken,
                                chainId: validToken.chainId,
                            })
                            setChainId(validToken.chainId)
                            didSetSellToken = true
                        }
                    }
                }
            }
        }

        // First check for the new format (to)
        // Handle 'to' token if provided
        const toToken = toParam ? parseTokenParam(toParam) : null
        if (toToken) {
            // Verify the token's chain is allowed if we have restricted chains
            const isChainAllowed = !supportedChains || supportedChains.includes(toToken.chainId)

            if (isChainAllowed) {
                const token = findTokenByAddress(toToken.tokenAddress, toToken.chainId)
                if (token) {
                    updateBuyData({
                        token,
                        chainId: toToken.chainId,
                    })
                    didSetBuyToken = true
                }
            } else {
                console.log(`Token to chain ${toToken.chainId} not allowed. Restricted to: ${supportedChains}`)
                // Find a valid token from allowed chains instead
                if (supportedChains && supportedChains.length > 0) {
                    // Use a different chain than sell if possible
                    const sellChainId = didSetSellToken ? sellData.chainId : null
                    const targetChainId = supportedChains.find(c => c !== sellChainId) || supportedChains[0]

                    const fallbackTokens = tokens.filter(t => t.chainId === targetChainId)
                    // Prefer stable coins
                    const stableToken = fallbackTokens.find(t => ['USDC', 'USDT', 'DAI', 'BUSD'].includes(t.symbol))

                    if (stableToken || fallbackTokens.length > 0) {
                        const validToken = stableToken || fallbackTokens[0]
                        console.log(`Using fallback buy token: ${validToken.symbol} (${validToken.chainId})`)
                        updateBuyData({
                            token: validToken,
                            chainId: validToken.chainId,
                        })
                        didSetBuyToken = true
                    }
                }
            }
        }
        // Otherwise, check for legacy format (buyChain/buyToken)
        else if (buyChainParam && buyTokenParam) {
            const buyChainId = parseInt(buyChainParam, 10)
            if (!isNaN(buyChainId) && Object.values(ChainId).includes(buyChainId)) {
                // Verify the chain is allowed if we have restricted chains
                const isChainAllowed = !supportedChains || supportedChains.includes(buyChainId)

                if (isChainAllowed) {
                    const token = findTokenByAddress(buyTokenParam as Address, buyChainId)
                    if (token) {
                        updateBuyData({
                            token,
                            chainId: buyChainId,
                        })
                        didSetBuyToken = true
                    }
                } else {
                    console.log(`Buy chain ${buyChainId} not allowed. Restricted to: ${supportedChains}`)
                    // Find a valid token from allowed chains instead
                    if (supportedChains && supportedChains.length > 0) {
                        // Use a different chain than sell if possible
                        const sellChainId = didSetSellToken ? sellData.chainId : null
                        const targetChainId = supportedChains.find(c => c !== sellChainId) || supportedChains[0]

                        const fallbackTokens = tokens.filter(t => t.chainId === targetChainId)
                        // Prefer stable coins
                        const stableToken = fallbackTokens.find(t => ['USDC', 'USDT', 'DAI', 'BUSD'].includes(t.symbol))

                        if (stableToken || fallbackTokens.length > 0) {
                            const validToken = stableToken || fallbackTokens[0]
                            console.log(`Using fallback buy token: ${validToken.symbol} (${validToken.chainId})`)
                            updateBuyData({
                                token: validToken,
                                chainId: validToken.chainId,
                            })
                            didSetBuyToken = true
                        }
                    }
                }
            }
        }

        // Update the config to restrict available networks if we have supported chains
        if (supportedChains) {
            updateConfig({
                restrictedChains: supportedChains,
            })
        }

        // Mark as initialized only if we found and set at least one token
        if (didSetSellToken || didSetBuyToken) {
            initialized.current = true
        }
    }, [
        fromParam,
        toParam,
        sellChainParam,
        buyChainParam,
        sellTokenParam,
        buyTokenParam,
        chainsParam,
        tokens,
        sellData,
        findTokenByAddress,
        updateSellData,
        updateBuyData,
        setChainId,
        updateConfig,
    ])

    return <ExchangePage hideAIInput={hideAIInput} />
}

export default EmbedPage
